import React, { useCallback, useEffect, useMemo, useState } from "react";
import { Dialog } from "components/main/utils/Dialog";
import { Button } from "components/common/Button";
import { SectionRule } from "components/common/SectionRule";
import { SearchBar } from "components/common/SearchBar";
import { Spinner } from "components/common/Spinner";
import { StatusChip } from "components/common/StatusChip";
import { Badge } from "components/common/Badge";
import { AssetIcon } from "components/common/AssetIcon";
import { plural } from "components/common/setUtils";
import { OrgMark } from "components/main/organizations/OrgMark";
import { ModelElementType } from "components/entities";
import { fetchModels } from "components/main/utils/models/ModelServices";
import {
  fetchTransferDestinations,
  handleTableOwnershipTransfer,
  type ModelDestination,
  type OrgDestination,
  type TransferDestinations,
} from "@/components/canvas/detailsSide/tableDetails/TableServices";
import { formatBytes } from "@/components/canvas/detailsSide/utils/formatBytes";
import {
  spaceColor,
  useOrgContextStore,
} from "components/main/stores/orgContextStore";
import { fetchOrgSpaces, type Space } from "@/lib/api/organizations";
import { resolveDatasetColor } from "@/lib/datasetColor";
import { showNotificationGlobal } from "stores/useNotificationStore";
import { createLogger } from "debug/DebugingLogger";
import TableChartOutlinedIcon from "@mui/icons-material/TableChartOutlined";
import CheckIcon from "@mui/icons-material/Check";

const log = createLogger("Move table");

/// The catalog is outside every org, so it has no `org_id` to key rows by.
const CATALOG_KEY = "__catalog__";

/// Refusals that describe where the table may go AT ALL, rather than a
/// destination being busy. A wall of those is noise, so the picker leaves them
/// out — with orgs as the first step that is one row per org, not one per model.
const HIDDEN_ORG_REFUSALS: ReadonlySet<string> = new Set([
  "org_boundary",
  "catalog_direction",
  "connection_org",
]);

/// An org's plan state, as Plan & usage shows it.
const ORG_REFUSAL_CHIP: Record<string, string> = {
  past_due: "past due",
  storage_full: "storage full",
};

const ORG_REFUSAL_COPY: Record<string, string> = {
  parent_lock: "external-source tables can't enter the catalog",
  connection_unresolvable: "this table's source connection can't be found",
  connection_org_unavailable:
    "couldn't verify the connection's organization — retry",
};

const MODEL_REFUSAL_COPY: Record<string, string> = {
  sealed: "sealed space — its data can't leave",
};

const orgKey = (row: Pick<OrgDestination, "org_id">) =>
  row.org_id ?? CATALOG_KEY;

interface MoveTableDialogProps {
  tableId: string;
  tableName: string;
  open: boolean;
  onOpenChange: React.Dispatch<React.SetStateAction<boolean>>;
}

/// D42 retired cross-user transfer: a table moves between MODELS, never between
/// people. The organization comes first because the org pair — not the model —
/// decides whether the table stays behind as an import and whether its links
/// survive.
export const MoveTableDialog = ({
  tableId,
  tableName,
  open,
  onOpenChange,
}: MoveTableDialogProps) => {
  const orgs = useOrgContextStore((s) => s.orgs);
  const orgsLoaded = useOrgContextStore((s) => s.orgsLoaded);
  const activeOrgId = useOrgContextStore((s) => s.activeOrgId);
  const activeOrgSpaces = useOrgContextStore((s) => s.spaces);

  const [models, setModels] = useState<ModelElementType[]>([]);
  const [verdicts, setVerdicts] = useState<TransferDestinations | null>(null);
  const [loading, setLoading] = useState(false);
  const [selectedOrg, setSelectedOrg] = useState<string | null>(null);
  const [selectedModel, setSelectedModel] = useState<string | null>(null);
  const [search, setSearch] = useState("");
  const [spacesByOrg, setSpacesByOrg] = useState<Record<string, Space[]>>({});
  const [moving, setMoving] = useState(false);

  const handleClose = useCallback(() => onOpenChange(false), [onOpenChange]);

  useEffect(() => {
    if (!open) return;
    let cancelled = false;
    setLoading(true);
    setSelectedOrg(null);
    setSelectedModel(null);
    setSearch("");
    setMoving(false);

    // UNSCOPED (D42): a personal-org table may move into any model the caller
    // manages, in any org, so narrowing to the active org context would hide
    // exactly the destinations the door allows. The server then judges each
    // candidate, so listing more never means offering more.
    fetchModels(false, { unscoped: true })
      .then(async (rows) => {
        if (cancelled) return;
        setModels(rows);
        const answer = await fetchTransferDestinations(
          tableId,
          rows.map((m) => m.model_id),
        );
        if (!cancelled) setVerdicts(answer);
      })
      .catch((error) => {
        log.error("Failed to load move destinations", error);
        if (!cancelled) {
          setModels([]);
          setVerdicts(null);
        }
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });

    return () => {
      cancelled = true;
    };
  }, [open, tableId]);

  // The store only holds the active org's tree, so any other org's space names
  // are fetched the first time one of its models is on screen.
  useEffect(() => {
    if (activeOrgId && activeOrgSpaces.length > 0) {
      setSpacesByOrg((prev) => ({ ...prev, [activeOrgId]: activeOrgSpaces }));
    }
  }, [activeOrgId, activeOrgSpaces]);

  useEffect(() => {
    if (!selectedOrg || selectedOrg === CATALOG_KEY) return;
    if (spacesByOrg[selectedOrg]) return;
    let cancelled = false;
    fetchOrgSpaces(selectedOrg).then((spaces) => {
      if (!cancelled)
        setSpacesByOrg((prev) => ({ ...prev, [selectedOrg]: spaces }));
    });
    return () => {
      cancelled = true;
    };
  }, [selectedOrg, spacesByOrg]);

  const modelsById = useMemo(
    () => new Map(models.map((m) => [m.model_id, m])),
    [models],
  );

  const sourceModel = verdicts
    ? modelsById.get(verdicts.source.model_id)
    : undefined;
  const sourceOrg = verdicts?.source.org_id
    ? orgs.find((o) => o.org_id === verdicts.source.org_id)
    : undefined;

  /// Receivable models per org, plus the source model as a disabled row in its
  /// own org so the one place a person looks for it is not silently empty.
  const modelsByOrg = useMemo(() => {
    const grouped = new Map<string, ModelDestination[]>();
    if (!verdicts) return grouped;
    for (const row of verdicts.models) {
      if (row.model_id === verdicts.source.model_id) continue;
      // A model the caller can't move into is the wrong kind of item, not a
      // destination that refuses — in a big org those would outnumber the real
      // choices.
      if (row.reason === "not_manager") continue;
      const key = orgKey(row);
      const existing = grouped.get(key);
      if (existing) existing.push(row);
      else grouped.set(key, [row]);
    }
    return grouped;
  }, [verdicts]);

  /// Orgs the table can actually go to. An org with nothing to receive it is
  /// left out; a real destination that refuses right now stays and says why.
  const visibleOrgs = useMemo(() => {
    if (!verdicts) return [];
    const rows = verdicts.orgs.filter((row) => {
      if (row.reason && HIDDEN_ORG_REFUSALS.has(row.reason)) return false;
      if (!row.can_receive) return true;
      return (modelsByOrg.get(orgKey(row)) ?? []).some((m) => m.can_receive);
    });
    const rank = (row: OrgDestination) => {
      if (row.org_id && row.org_id === verdicts.source.org_id) return 0;
      if (row.is_catalog) return 2;
      return 1;
    };
    return rows.sort((a, b) => rank(a) - rank(b));
  }, [verdicts, modelsByOrg]);

  const selectedOrgRow = visibleOrgs.find((row) => orgKey(row) === selectedOrg);
  const detaches = selectedOrgRow?.detaches_source ?? false;

  const orgModels = selectedOrg ? (modelsByOrg.get(selectedOrg) ?? []) : [];
  const filteredModels = useMemo(() => {
    const rows = orgModels;
    if (!search.trim()) return rows;
    const q = search.toLowerCase();
    return rows.filter((row) =>
      (modelsById.get(row.model_id)?.name ?? "").toLowerCase().includes(q),
    );
  }, [orgModels, search, modelsById]);

  const severed = detaches ? (verdicts?.severed_links ?? []) : [];
  const hiddenSeveredCount = detaches
    ? (verdicts?.hidden_severed_link_count ?? 0)
    : 0;
  const severedLinkCount =
    severed.reduce((sum, t) => sum + t.link_count, 0) + hiddenSeveredCount;

  /// Only ever called once `orgsLoaded` — before that the list is "unknown",
  /// and a miss would render as a placeholder that reads like a real name.
  const orgName = (row: OrgDestination) =>
    row.is_catalog
      ? "Public catalog"
      : (orgs.find((o) => o.org_id === row.org_id)?.name ??
        "Unknown organization");

  const move = async () => {
    if (!verdicts || !selectedModel || moving) return;
    setMoving(true);
    const result = await handleTableOwnershipTransfer(
      tableId,
      verdicts.source.model_id,
      selectedModel,
    );
    setMoving(false);
    if (result.success) {
      showNotificationGlobal(`"${tableName}" moved`);
      handleClose();
    } else {
      showNotificationGlobal(result.message, "error");
    }
  };

  return (
    <Dialog
      open={open}
      title="Move table"
      onClose={handleClose}
      autoSpacing={false}
      className="w-150!"
      actions={
        <>
          <Button variant="secondary" onClick={handleClose}>
            Cancel
          </Button>
          <Button
            variant="primary"
            disabled={!selectedModel || moving}
            loading={moving}
            onClick={move}
          >
            {severedLinkCount > 0
              ? `Move and remove ${plural(severedLinkCount, "link")}`
              : "Move table"}
          </Button>
        </>
      }
    >
      {loading || !orgsLoaded || !verdicts ? (
        <div className="flex items-center justify-center py-10">
          <Spinner />
        </div>
      ) : (
        <div className="flex flex-col gap-4">
          <div className="flex flex-col gap-2">
            <SectionRule label="From" />
            <div className="flex items-center gap-2 rounded-app border border-border bg-bg px-3 py-2">
              <TableChartOutlinedIcon className="icon-sm text-fg-muted" />
              <span className="text-fg-bold truncate">{tableName}</span>
              <span className="text-fg-muted">in</span>
              {sourceModel && (
                <AssetIcon
                  size="sm"
                  color={sourceModel.color}
                  iconIndex={sourceModel.icon}
                  className="w-3.5! h-3.5!"
                />
              )}
              <span className="text-fg-medium truncate">
                {sourceModel?.name ?? "This model"}
              </span>
              <span className="ml-auto flex items-center gap-1.5">
                {sourceOrg ? (
                  <>
                    <OrgMark org={sourceOrg} size="sm" />
                    <span className="text-fg-medium truncate">
                      {sourceOrg.name}
                    </span>
                  </>
                ) : verdicts.source.is_catalog ? (
                  <StatusChip tone="warning">Catalog</StatusChip>
                ) : null}
              </span>
            </div>
          </div>

          <div className="flex flex-col gap-2">
            <SectionRule label="To organization" />
            <div role="radiogroup" aria-label="Destination organization">
              {visibleOrgs.map((row) => {
                const key = orgKey(row);
                const org = orgs.find((o) => o.org_id === row.org_id);
                const isSource =
                  !!row.org_id && row.org_id === verdicts.source.org_id;
                return (
                  <button
                    key={key}
                    type="button"
                    role="radio"
                    aria-checked={selectedOrg === key}
                    aria-disabled={!row.can_receive}
                    disabled={!row.can_receive}
                    onClick={() => {
                      setSelectedOrg(key);
                      setSelectedModel(null);
                      setSearch("");
                    }}
                    className={`flex h-9 w-full items-center gap-2 rounded-app border px-3 text-left transition-colors ${
                      selectedOrg === key
                        ? "border-accent bg-selected-light"
                        : "border-transparent"
                    } ${row.can_receive ? "hover:bg-bg-hover cursor-pointer" : "cursor-default"}`}
                  >
                    {org ? (
                      <OrgMark org={org} size="md" />
                    ) : (
                      <span
                        aria-hidden
                        className="flex size-6 shrink-0 items-center justify-center rounded-full border border-border bg-accent-subtle text-fg-muted"
                      >
                        <TableChartOutlinedIcon className="icon-xs" />
                      </span>
                    )}
                    <span
                      className={`truncate ${row.can_receive ? "text-fg-medium" : "text-fg-muted"}`}
                    >
                      {orgName(row)}
                    </span>
                    {org?.plan_name && <Badge label={org.plan_name} />}
                    {row.is_catalog && (
                      <StatusChip tone="muted">Unbilled</StatusChip>
                    )}
                    {isSource && <StatusChip tone="muted">Current</StatusChip>}
                    <span className="ml-auto flex items-center gap-2">
                      {row.reason && ORG_REFUSAL_CHIP[row.reason] ? (
                        <StatusChip tone="error">
                          {ORG_REFUSAL_CHIP[row.reason]}
                        </StatusChip>
                      ) : row.reason ? (
                        <span className="text-fg-muted-regular text-small">
                          {ORG_REFUSAL_COPY[row.reason] ?? "unavailable"}
                        </span>
                      ) : selectedOrg === key ? (
                        <CheckIcon className="icon-sm text-accent" />
                      ) : null}
                    </span>
                  </button>
                );
              })}
              {visibleOrgs.length === 0 && (
                <p className="text-fg-muted-regular py-3 text-center">
                  This table has nowhere to go — no organization you belong to
                  can receive it.
                </p>
              )}
            </div>
          </div>

          <div className="flex flex-col gap-2">
            <SectionRule
              label="To model"
              trailing={
                selectedOrgRow
                  ? detaches
                    ? `leaves ${sourceOrg?.name ?? "this organization"}`
                    : selectedOrgRow.is_catalog
                      ? "publish"
                      : "same org"
                  : undefined
              }
            />
            {!selectedOrg ? (
              <p className="text-fg-muted-regular rounded-app border border-dashed border-border px-3 py-2">
                Choose an organization first
              </p>
            ) : (
              <>
                {orgModels.length > 3 && (
                  <SearchBar
                    value={search}
                    onChange={setSearch}
                    placeholder="Search models"
                  />
                )}
                <div className="max-h-52 overflow-y-auto" role="radiogroup">
                  {filteredModels.map((row) => {
                    const model = modelsById.get(row.model_id);
                    const space = (spacesByOrg[selectedOrg] ?? []).find(
                      (s) => s.space_id === model?.space_id,
                    );
                    return (
                      <button
                        key={row.model_id}
                        type="button"
                        role="radio"
                        aria-checked={selectedModel === row.model_id}
                        aria-disabled={!row.can_receive}
                        disabled={!row.can_receive}
                        onClick={() => setSelectedModel(row.model_id)}
                        className={`flex h-8 w-full items-center gap-2 rounded-app border px-3 text-left transition-colors ${
                          selectedModel === row.model_id
                            ? "border-accent bg-selected-light"
                            : "border-transparent"
                        } ${row.can_receive ? "hover:bg-bg-hover cursor-pointer" : "cursor-default"}`}
                      >
                        <AssetIcon
                          size="sm"
                          color={model?.color}
                          iconIndex={model?.icon}
                          className="w-3.5! h-3.5!"
                        />
                        <span
                          className={`truncate ${row.can_receive ? "text-fg-medium" : "text-fg-muted"}`}
                        >
                          {model?.name || "Unnamed model"}
                        </span>
                        {space && (
                          <span
                            className="flex shrink-0 items-center gap-1.5 font-mono text-fg-muted"
                            style={{ fontSize: "var(--font-size-x-small)" }}
                            title={space.name}
                          >
                            <span
                              aria-hidden
                              className="h-1.5 w-1.5 shrink-0 rounded-app"
                              style={{
                                backgroundColor: resolveDatasetColor(
                                  spaceColor(space),
                                ),
                              }}
                            />
                            {space.name}
                          </span>
                        )}
                        <span className="ml-auto flex items-center gap-2">
                          {row.reason ? (
                            <span className="text-fg-muted-regular text-small">
                              {MODEL_REFUSAL_COPY[row.reason] ?? "unavailable"}
                            </span>
                          ) : selectedModel === row.model_id ? (
                            <CheckIcon className="icon-sm text-accent" />
                          ) : null}
                        </span>
                      </button>
                    );
                  })}
                  {filteredModels.length === 0 && (
                    <p className="text-fg-muted-regular py-3 text-center">
                      {search.trim()
                        ? "No models match your search."
                        : "No models here can receive this table."}
                    </p>
                  )}
                </div>
              </>
            )}
          </div>

          {selectedOrgRow && selectedModel && (
            <AfterTheMove
              sourceModelName={sourceModel?.name}
              destination={selectedOrgRow}
              destinationName={orgName(selectedOrgRow)}
              detaches={detaches}
              severed={severed}
              hiddenSeveredCount={hiddenSeveredCount}
              severedLinkCount={severedLinkCount}
              totalLinkCount={verdicts.total_link_count}
              tableSizeBytes={verdicts.table_size_bytes}
            />
          )}
        </div>
      )}
    </Dialog>
  );
};

const Fact: React.FC<{ label: string; children: React.ReactNode }> = ({
  label,
  children,
}) => (
  <div className="flex items-start gap-2 border-t border-border px-3 py-2 first:border-t-0">
    <span
      className="text-fg-muted w-24 shrink-0 pt-0.5 font-mono uppercase"
      style={{ fontSize: "var(--font-size-x-small)" }}
    >
      {label}
    </span>
    <span className="min-w-0 flex-1">{children}</span>
  </div>
);

const LinkCount: React.FC<{ count: number }> = ({ count }) => (
  <span
    className="text-fg-muted ml-auto shrink-0 font-mono"
    style={{ fontSize: "var(--font-size-x-small)" }}
  >
    {plural(count, "link")}
  </span>
);

/// The review block IS the confirmation — there is no second dialog. Amber only
/// when the move takes the table out of its organization, which is the one case
/// that destroys something.
const AfterTheMove: React.FC<{
  sourceModelName?: string;
  destination: OrgDestination;
  destinationName: string;
  detaches: boolean;
  severed: TransferDestinations["severed_links"];
  hiddenSeveredCount: number;
  severedLinkCount: number;
  /// Undefined when the server could not read it, which reads as "some, all
  /// surviving" rather than as none.
  totalLinkCount?: number;
  tableSizeBytes: number;
}> = ({
  sourceModelName,
  destination,
  destinationName,
  detaches,
  severed,
  hiddenSeveredCount,
  severedLinkCount,
  totalLinkCount,
  tableSizeBytes,
}) => (
  <div className="flex flex-col gap-2">
    <SectionRule label="After the move" />
    <div
      className={`rounded-app border ${detaches ? "border-warning bg-warning-bg" : "border-border bg-bg"}`}
    >
      <Fact label={sourceModelName ?? "This model"}>
        {detaches ? (
          <>
            <span className="text-fg-bold">Removed</span>{" "}
            <span className="text-fg-muted">· views included</span>
          </>
        ) : (
          <>
            <span className="text-fg-bold">Kept</span>{" "}
            <span className="text-fg-muted">· as import</span>
          </>
        )}
      </Fact>
      <Fact label="Links">
        {totalLinkCount === 0 && severedLinkCount === 0 ? (
          <span className="text-fg-bold">None</span>
        ) : severedLinkCount === 0 ? (
          <span className="text-fg-bold">Kept</span>
        ) : (
          <>
            <span className="text-fg-bold">Links will be removed</span>
            <div className="mt-1.5 flex flex-col gap-0.5">
              {severed.map((t) => (
                <div key={t.table_id} className="flex items-center gap-1.5">
                  <span className="truncate">{t.table_name}</span>
                  <span
                    className="text-fg-muted font-mono"
                    style={{ fontSize: "var(--font-size-x-small)" }}
                  >
                    · {t.model_name}
                  </span>
                  <LinkCount count={t.link_count} />
                </div>
              ))}
              {hiddenSeveredCount > 0 && (
                <div className="flex items-center gap-1.5">
                  <span className="text-fg-muted truncate">
                    Tables you can't see
                  </span>
                  <LinkCount count={hiddenSeveredCount} />
                </div>
              )}
            </div>
          </>
        )}
      </Fact>
      <Fact label="Storage">
        {destination.is_catalog ? (
          <>
            {formatBytes(tableSizeBytes)} freed{" "}
            <span className="text-fg-muted">· catalog is unbilled</span>
          </>
        ) : detaches ? (
          <>
            {formatBytes(tableSizeBytes)} →{" "}
            <span className="text-fg-bold">{destinationName}</span>
          </>
        ) : (
          <span className="text-fg-bold">Unchanged</span>
        )}
      </Fact>
    </div>
  </div>
);
